import { animate, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";

/**
 * Count-up.
 *
 * Set large in the display serif with tabular figures, so the number does not
 * jitter horizontally while it counts.
 *
 * The suffix renders inline at full size, not as a raised superscript in an
 * accent colour. Four figures where one had a raised blue "+", one had a small
 * grey "m" and two had nothing read as three different treatments rather than
 * one system. Units now live in the label, which leaves "+" as the only inline
 * qualifier, and it earns its place: the source says "at least 60".
 *
 * Accessibility:
 * - Runs once, as the element approaches the viewport, so fast navigation
 *   movements near the threshold cannot restart it.
 * - The animating figure is aria-hidden; a visually hidden element carries the
 *   final value and its label, so screen readers never hear intermediate numbers.
 * - `prefers-reduced-motion` renders the final value immediately.
 * - If the tab is backgrounded mid-count, rAF throttling would freeze a partial
 *   figure on screen, so the counter snaps to the real number instead.
 */
export function ImpactCounter({
  value,
  suffix = "",
  label,
  context,
  asOf,
  tone = "dark",
}: {
  value: number;
  suffix?: string;
  label: string;
  context?: string;
  asOf?: string;
  tone?: "light" | "dark";
}) {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { once: true, amount: 0.15, margin: "160px 0px" });
  const reduceMotion = useReducedMotion();
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (reduceMotion) {
      setDisplay(value);
      return;
    }

    if (!inView) {
      // IntersectionObserver is an enhancement. If it is unavailable or never
      // initialises, do not leave a real figure displaying as zero forever.
      const fallback = window.setTimeout(() => setDisplay(value), 1800);
      return () => window.clearTimeout(fallback);
    }

    const controls = animate(0, value, {
      // Long enough to read as counting, short enough that the figure is
      // settled before somebody has finished scanning the label.
      duration: 1.15,
      ease: [0.16, 1, 0.3, 1],
      onUpdate: (latest) => setDisplay(Math.round(latest)),
      onComplete: () => setDisplay(value),
    });

    const onVisibilityChange = () => {
      if (document.hidden) {
        controls.stop();
        setDisplay(value);
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      controls.stop();
      setDisplay(value);
    };
  }, [inView, reduceMotion, value]);

  const light = tone === "light";

  return (
    <div ref={ref} className="flex flex-col">
      <p
        aria-hidden="true"
        className={`font-serif leading-[0.9] tabular-nums ${light ? "text-white" : "text-midnight"}`}
        style={{ fontSize: "clamp(3rem, 1.9rem + 4vw, 4.75rem)" }}
      >
        {new Intl.NumberFormat("en").format(display)}
        {suffix}
      </p>

      <p className="sr-only">
        {new Intl.NumberFormat("en").format(value)}
        {suffix}, {label}
        {asOf ? `, as of ${asOf}` : ""}
      </p>

      <span
        aria-hidden="true"
        className={`mt-6 block h-px w-10 ${light ? "bg-white/25" : "bg-line-strong"}`}
      />

      <p
        className={`mt-5 text-[0.9375rem] font-medium leading-snug ${
          light ? "text-white/90" : "text-ink"
        }`}
      >
        {label}
      </p>
      {context ? (
        <p className={`mt-2 text-[0.8125rem] leading-snug ${light ? "text-white/55" : "text-muted"}`}>
          {context}
        </p>
      ) : null}
    </div>
  );
}
